import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import LandingPage from "@/pages/LandingPage";
import AnalyticsConsent from "@/components/AnalyticsConsent";

const VideotecaPage = lazy(() => import("@/pages/VideotecaPage"));
const McpPublicGuidePage = lazy(() => import("@/pages/McpPublicGuidePage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const TendersPage = lazy(() => import("@/pages/TendersPage"));
const PostulacionesPage = lazy(() => import("@/pages/PostulacionesPage"));
const RulesPage = lazy(() => import("@/pages/RulesPage"));
const UsersPage = lazy(() => import("@/pages/UsersPage"));
const TenantsPage = lazy(() => import("@/pages/TenantsPage"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const BrandingPage = lazy(() => import("@/pages/BrandingPage"));
const SyncStatusPage = lazy(() => import("@/pages/SyncStatusPage"));
const AuditLogsPage = lazy(() => import("@/pages/AuditLogsPage"));
const DocumentosPage = lazy(() => import("@/pages/DocumentosPage"));
const TicketsPage = lazy(() => import("@/pages/TicketsPage"));
const ClientesPage = lazy(() => import("@/pages/ClientesPage"));
const MercadoPage = lazy(() => import("@/pages/MercadoPage"));
const PropuestasPage = lazy(() => import("@/pages/PropuestasPage"));
const CompraAgilPage = lazy(() => import("@/pages/CompraAgilPage"));
const OrdenesCompraPage = lazy(() => import("@/pages/OrdenesCompraPage"));
const TratoDirectoPage = lazy(() => import("@/pages/TratoDirectoPage"));
const ConvenioMarcoPage = lazy(() => import("@/pages/ConvenioMarcoPage"));
const ContactoEmailPage = lazy(() => import("@/pages/ContactoEmailPage"));
const ContactoPage = lazy(() => import("@/pages/ContactoPage"));
const GraciasPage = lazy(() => import("@/pages/GraciasPage"));
const GraciasEmailPage = lazy(() => import("@/pages/GraciasEmailPage"));
const GraciasPlanPage = lazy(() => import("@/pages/GraciasPlanPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogAdminPage = lazy(() => import("@/pages/BlogAdminPage"));
const OportunidadesAnticipadasPage = lazy(() => import("@/pages/OportunidadesAnticipadasPage"));
const AnalisisHistoricoPage = lazy(() => import("@/pages/AnalisisHistoricoPage"));
const MiPosicionamientoPage = lazy(() => import("@/pages/MiPosicionamientoPage"));
const DataQualityPage = lazy(() => import("@/pages/DataQualityPage"));
const AnexosPage = lazy(() => import("@/pages/AnexosPage"));
const PrecisionPage = lazy(() => import("@/pages/PrecisionPage"));
const McpSettingsPage = lazy(() => import("@/pages/McpSettingsPage"));
const PostAdjudicacionPage = lazy(() => import("@/pages/PostAdjudicacionPage"));
const ApprovalsPage = lazy(() => import("@/pages/ApprovalsPage"));
const AppShell = lazy(() => import("@/components/AppShell"));

const PrivateRoute = ({ children, roles }) => {
    const { user } = useAuth();
    if (!user) return <Navigate to="/acceso" replace />;
    if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
    return children;
};

const Shell = ({ children }) => <AppShell>{children}</AppShell>;
const PageLoading = () => (
    <div className="min-h-[40vh] grid place-items-center" role="status" aria-live="polite">
        <span className="text-sm font-medium text-slate-500">Cargando LicitX…</span>
    </div>
);

// Toaster wired to the active theme so light mode gets a clean light toast.
const ThemedToaster = () => {
    const { theme } = useTheme();
    return <Toaster theme={theme} position="top-right" closeButton />;
};

function App() {
    return (
        <HelmetProvider>
        <ThemeProvider>
            <AuthProvider>
                <BrandingProvider>
                    <div className="App">
                        <BrowserRouter>
                            <Suspense fallback={<PageLoading />}>
                            <Routes>
                                {/* Public marketing site */}
                                <Route path="/" element={<LandingPage />} />
                                <Route path="/videos" element={<VideotecaPage />} />
                                <Route path="/mcp" element={<McpPublicGuidePage />} />
                                <Route path="/contacto-email" element={<ContactoEmailPage />} />
                                <Route path="/contacto" element={<ContactoPage />} />
                                <Route path="/gracias" element={<GraciasPage />} />
                                <Route path="/gracias-email" element={<GraciasEmailPage />} />
                                <Route path="/gracias/:plan" element={<GraciasPlanPage />} />
                                {/* Blog público — sin autenticación */}
                                <Route path="/blog" element={<BlogPage />} />
                                <Route path="/blog/:slug" element={<BlogPage />} />

                                {/* Auth pages */}
                                <Route path="/acceso" element={<LoginPage />} />
                                {/* Legacy redirects so old bookmarks keep working */}
                                <Route path="/login" element={<Navigate to="/acceso" replace />} />

                                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                                <Route path="/reset-password" element={<ResetPasswordPage />} />

                                {/* Authenticated app — namespaced under /app */}
                                <Route path="/app" element={<PrivateRoute><Shell><DashboardPage /></Shell></PrivateRoute>} />
                                <Route path="/app/licitaciones" element={<PrivateRoute><Shell><TendersPage /></Shell></PrivateRoute>} />
                                <Route path="/app/postulaciones" element={<PrivateRoute><Shell><PostulacionesPage /></Shell></PrivateRoute>} />
                                <Route path="/app/reglas" element={<PrivateRoute roles={["admin"]}><Shell><RulesPage /></Shell></PrivateRoute>} />
                                <Route path="/app/sincronizacion" element={<PrivateRoute roles={["admin"]}><Shell><SyncStatusPage /></Shell></PrivateRoute>} />
                                <Route path="/app/auditoria" element={<PrivateRoute roles={["admin", "super_admin"]}><Shell><AuditLogsPage /></Shell></PrivateRoute>} />
                                <Route path="/app/usuarios" element={<PrivateRoute roles={["admin", "super_admin"]}><Shell><UsersPage /></Shell></PrivateRoute>} />
                                <Route path="/app/marca" element={<PrivateRoute roles={["admin"]}><Shell><BrandingPage /></Shell></PrivateRoute>} />
                                <Route path="/app/empresas" element={<PrivateRoute roles={["super_admin"]}><Shell><TenantsPage /></Shell></PrivateRoute>} />
                                <Route path="/app/notificaciones" element={<PrivateRoute><Shell><NotificationsPage /></Shell></PrivateRoute>} />
                                <Route path="/app/perfil" element={<PrivateRoute><Shell><ProfilePage /></Shell></PrivateRoute>} />
                                <Route path="/app/documentos" element={<PrivateRoute><Shell><DocumentosPage /></Shell></PrivateRoute>} />
                                <Route path="/app/tickets" element={<PrivateRoute><Shell><TicketsPage /></Shell></PrivateRoute>} />
                                <Route path="/app/clientes" element={<PrivateRoute><Shell><ClientesPage /></Shell></PrivateRoute>} />
                                <Route path="/app/mercado" element={<PrivateRoute><Shell><MercadoPage /></Shell></PrivateRoute>} />
                                <Route path="/app/propuestas" element={<PrivateRoute><Shell><PropuestasPage /></Shell></PrivateRoute>} />
                                <Route path="/app/compra-agil" element={<PrivateRoute><Shell><CompraAgilPage /></Shell></PrivateRoute>} />
                                <Route path="/app/ordenes-compra" element={<PrivateRoute><Shell><OrdenesCompraPage /></Shell></PrivateRoute>} />
                                <Route path="/app/trato-directo" element={<PrivateRoute><Shell><TratoDirectoPage /></Shell></PrivateRoute>} />
                                <Route path="/app/convenio-marco" element={<PrivateRoute><Shell><ConvenioMarcoPage /></Shell></PrivateRoute>} />
                                <Route path="/app/oportunidades-anticipadas" element={<PrivateRoute><Shell><OportunidadesAnticipadasPage /></Shell></PrivateRoute>} />
                                <Route path="/app/analisis-historico" element={<PrivateRoute><Shell><AnalisisHistoricoPage /></Shell></PrivateRoute>} />
                                <Route path="/app/mi-posicionamiento" element={<PrivateRoute><Shell><MiPosicionamientoPage /></Shell></PrivateRoute>} />
                                <Route path="/app/confianza-datos" element={<PrivateRoute roles={["super_admin"]}><Shell><DataQualityPage /></Shell></PrivateRoute>} />
                                <Route path="/app/anexos" element={<PrivateRoute><Shell><AnexosPage /></Shell></PrivateRoute>} />
                                <Route path="/app/precision" element={<PrivateRoute roles={["super_admin"]}><Shell><PrecisionPage /></Shell></PrivateRoute>} />
                                <Route path="/app/mcp" element={<PrivateRoute roles={["admin", "super_admin"]}><Shell><McpSettingsPage /></Shell></PrivateRoute>} />
                                <Route path="/app/post-adjudicacion" element={<PrivateRoute><Shell><PostAdjudicacionPage /></Shell></PrivateRoute>} />
                                <Route path="/app/aprobaciones" element={<PrivateRoute><Shell><ApprovalsPage /></Shell></PrivateRoute>} />
                                {/* Blog admin — solo editor y super_admin */}
                                <Route path="/app/blog" element={<PrivateRoute roles={["editor", "super_admin"]}><Shell><BlogAdminPage /></Shell></PrivateRoute>} />

                                {/* Legacy /licitaciones redirect to /app/licitaciones for old bookmarks */}
                                <Route path="/licitaciones" element={<Navigate to="/app/licitaciones" replace />} />
                                <Route path="/postulaciones" element={<Navigate to="/app/postulaciones" replace />} />
                                <Route path="/reglas" element={<Navigate to="/app/reglas" replace />} />
                                <Route path="/sincronizacion" element={<Navigate to="/app/sincronizacion" replace />} />
                                <Route path="/auditoria" element={<Navigate to="/app/auditoria" replace />} />
                                <Route path="/usuarios" element={<Navigate to="/app/usuarios" replace />} />
                                <Route path="/marca" element={<Navigate to="/app/marca" replace />} />
                                <Route path="/empresas" element={<Navigate to="/app/empresas" replace />} />
                                <Route path="/notificaciones" element={<Navigate to="/app/notificaciones" replace />} />
                                <Route path="/perfil" element={<Navigate to="/app/perfil" replace />} />

                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                            </Suspense>
                        </BrowserRouter>
                        <ThemedToaster />
                        <AnalyticsConsent />
                    </div>
                </BrandingProvider>
            </AuthProvider>
        </ThemeProvider>
        </HelmetProvider>
    );
}

export default App;
